import React, {useEffect, useState} from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    Paper,
    TextField,
    Typography,
    Card,
    CardContent,
    Stack,
    Divider,
    useMediaQuery,
    useTheme,
    Avatar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SaveIcon from '@mui/icons-material/Save';
import {Campaign, CalendarMonth, AccessTime, LocalOffer, InfoOutlined, Image} from '@mui/icons-material';
import dayjs from 'dayjs';
import MenuApi from 'src/api/menuApi/menuApi';

const KampanyaYonetimi = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [kampanyalar, setKampanyalar] = useState<any[]>([]);
    const [urunler, setUrunler] = useState<any[]>([]);
    const [openModal, setOpenModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [selectedKampanya, setSelectedKampanya] = useState<any>({
        isim: '',
        metin: '',
        kampanya_gorsel: '',
        fiyat: 0,
        kampanya_baslangic_tarihi: new Date().toISOString().split('T')[0],
        kampanya_bitis_tarihi: new Date().toISOString().split('T')[0],
        kampanya_aktif_baslangic: '08:00',
        kampanya_aktif_bitis: '23:59',
        aktif: true,
        urunler: [],
    });

    const fetchData = async () => {
        try {
            const [kData, uData] = await Promise.all([
                MenuApi.kampanya.list(),
                MenuApi.urun.list(),
            ]);
            setKampanyalar(kData);
            setUrunler(uData);
        } catch (error) {
            console.error("Veri çekme hatası:", error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSave = async () => {
        try {
            const formData = new FormData();
            formData.append('isim', selectedKampanya.isim);
            formData.append('metin', selectedKampanya.metin || '');
            formData.append('fiyat', String(selectedKampanya.fiyat));
            formData.append('kampanya_baslangic_tarihi', selectedKampanya.kampanya_baslangic_tarihi);
            formData.append('kampanya_bitis_tarihi', selectedKampanya.kampanya_bitis_tarihi);
            formData.append('kampanya_aktif_baslangic', selectedKampanya.kampanya_aktif_baslangic);
            formData.append('kampanya_aktif_bitis', selectedKampanya.kampanya_aktif_bitis);
            formData.append('aktif', String(selectedKampanya.aktif));

            const urunIds = (selectedKampanya.urunler || []).map((u: any) => typeof u === 'object' ? u.id : u);
            urunIds.forEach((id: number) => formData.append('urunler', String(id)));

            if (selectedFile) {
                formData.append('kampanya_gorsel', selectedFile);
            }

            if (selectedKampanya.id) {
                await MenuApi.kampanya.updatePartial(selectedKampanya.id, formData);
            } else {
                await MenuApi.kampanya.create(formData);
            }
            setOpenModal(false);
            setSelectedFile(null);
            setPreviewUrl(null);
            fetchData();
        } catch (error) {
            console.error("Kampanya kaydetme hatası:", error);
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm("Bu kampanyayı silmek istediğinize emin misiniz?")) {
            try {
                await MenuApi.kampanya.delete(id);
                fetchData();
            } catch (error) {
                console.error("Kampanya silme hatası:", error);
            }
        }
    };

    return (
        <Box sx={{ mt: 1 }}>
            <Box sx={{
                mb: 3,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                p: 1,
                bgcolor: '#f8f9fa',
                borderRadius: 1
            }}>
                <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<AddIcon/>}
                    sx={{ minWidth: 200, height: 40 }}
                    onClick={() => {
                        setSelectedKampanya({
                            isim: '',
                            metin: '',
                            kampanya_gorsel: '',
                            fiyat: 0,
                            kampanya_baslangic_tarihi: new Date().toISOString().split('T')[0],
                            kampanya_bitis_tarihi: new Date().toISOString().split('T')[0],
                            kampanya_aktif_baslangic: '08:00',
                            kampanya_aktif_bitis: '23:59',
                            aktif: true,
                            urunler: [],
                        });
                        setSelectedFile(null);
                        setPreviewUrl(null);
                        setOpenModal(true);
                    }}>
                    Yeni Kampanya
                </Button>
            </Box>

            <Grid container spacing={2}>
                {kampanyalar.map((row: any) => (
                    <Grid item xs={12} sm={6} md={4} key={row.id}>
                        <Card sx={{ 
                            height: '100%', 
                            display: 'flex', 
                            flexDirection: 'column',
                            borderRadius: '16px',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                            },
                            border: row.aktif ? 'none' : '1px solid rgba(0,0,0,0.05)',
                            opacity: row.aktif ? 1 : 0.8
                        }}>
                            <Box sx={{ position: 'relative', pt: '56.25%', bgcolor: 'grey.100' }}>
                                {row.kampanya_gorsel ? (
                                    <img 
                                        src={row.kampanya_gorsel} 
                                        alt={row.isim}
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Image sx={{ fontSize: 48, color: 'grey.300' }} />
                                    </Box>
                                )}
                                <Chip 
                                    label={row.aktif ? 'AKTİF' : 'PASİF'} 
                                    color={row.aktif ? 'success' : 'default'}
                                    size="small"
                                    sx={{ position: 'absolute', top: 12, right: 12, fontWeight: 900, borderRadius: '8px' }}
                                />
                            </Box>
                            <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                                <Typography variant="h6" fontWeight={800} gutterBottom sx={{ lineHeight: 1.2 }}>
                                    {row.isim}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ 
                                    mb: 2, 
                                    display: '-webkit-box', 
                                    WebkitLineClamp: 2, 
                                    WebkitBoxOrient: 'vertical', 
                                    overflow: 'hidden',
                                    height: '3em'
                                }}>
                                    {row.metin || 'Açıklama belirtilmemiş.'}
                                </Typography>

                                <Stack spacing={1.5}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <LocalOffer color="primary" fontSize="small" />
                                        <Typography variant="h6" fontWeight={900} color="primary">
                                            {Number(row.fiyat).toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                                        </Typography>
                                    </Stack>

                                    <Divider />

                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CalendarMonth fontSize="small" sx={{ opacity: 0.6 }} />
                                        <Typography variant="caption" fontWeight={700}>
                                            {dayjs(row.kampanya_baslangic_tarihi).format('DD MMM')} - {dayjs(row.kampanya_bitis_tarihi).format('DD MMM')}
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <AccessTime fontSize="small" sx={{ opacity: 0.6 }} />
                                        <Typography variant="caption" fontWeight={700}>
                                            {row.kampanya_aktif_baslangic} - {row.kampanya_aktif_bitis}
                                        </Typography>
                                    </Stack>
                                </Stack>

                                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                    <IconButton 
                                        size="small" 
                                        sx={{ bgcolor: 'primary.light', color: 'primary.main', '&:hover': { bgcolor: '#c5e1a5' } }}
                                        onClick={() => {
                                            const selectedUrunObjs = urunler.filter(u => (row.urunler || []).includes(u.id));
                                            setSelectedKampanya({...row, urunler: selectedUrunObjs});
                                            setSelectedFile(null);
                                            setPreviewUrl(null);
                                            setOpenModal(true);
                                        }}
                                    >
                                        <EditIcon fontSize="small"/>
                                    </IconButton>
                                    <IconButton 
                                        size="small" 
                                        sx={{ bgcolor: 'error.light', color: 'error.main', '&:hover': { bgcolor: '#ffcdd2' } }}
                                        onClick={() => handleDelete(row.id)}
                                    >
                                        <DeleteIcon fontSize="small"/>
                                    </IconButton>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Dialog 
                open={openModal} 
                onClose={() => setOpenModal(false)} 
                fullWidth 
                maxWidth="md"
                PaperProps={{
                    sx: { borderRadius: '24px' }
                }}
            >
                <DialogTitle sx={{ p: 3, pb: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Campaign color="primary" />
                        <Typography variant="h6" fontWeight={800} sx={{ textTransform: 'uppercase' }}>
                            {selectedKampanya.id ? 'Kampanyayı Düzenle' : 'Yeni Kampanya Oluştur'}
                        </Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ p: 3, pt: 1 }}>
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={8}>
                            <TextField
                                fullWidth
                                label="Kampanya İsmi"
                                variant="outlined"
                                value={selectedKampanya.isim}
                                onChange={(e) => setSelectedKampanya({...selectedKampanya, isim: e.target.value})}
                                InputProps={{ sx: { borderRadius: '12px' } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Fiyat (₺)"
                                type="number"
                                value={selectedKampanya.fiyat}
                                onChange={(e) => setSelectedKampanya({...selectedKampanya, fiyat: e.target.value})}
                                InputProps={{ sx: { borderRadius: '12px' } }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Kampanya Metni"
                                multiline
                                rows={3}
                                value={selectedKampanya.metin || ''}
                                onChange={(e) => setSelectedKampanya({...selectedKampanya, metin: e.target.value})}
                                InputProps={{ sx: { borderRadius: '12px' } }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }}><Chip label="Kampanya Takvimi" size="small" variant="outlined" sx={{ fontWeight: 700 }} /></Divider>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Başlangıç Tarihi"
                                type="date"
                                value={selectedKampanya.kampanya_baslangic_tarihi}
                                onChange={(e) => setSelectedKampanya({
                                    ...selectedKampanya,
                                    kampanya_baslangic_tarihi: e.target.value
                                })}
                                InputLabelProps={{shrink: true}}
                                InputProps={{ sx: { borderRadius: '12px' } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Bitiş Tarihi"
                                type="date"
                                value={selectedKampanya.kampanya_bitis_tarihi}
                                onChange={(e) => setSelectedKampanya({
                                    ...selectedKampanya,
                                    kampanya_bitis_tarihi: e.target.value
                                })}
                                InputLabelProps={{shrink: true}}
                                InputProps={{ sx: { borderRadius: '12px' } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Günlük Aktif Başlangıç"
                                type="time"
                                value={selectedKampanya.kampanya_aktif_baslangic}
                                onChange={(e) => setSelectedKampanya({
                                    ...selectedKampanya,
                                    kampanya_aktif_baslangic: e.target.value
                                })}
                                InputLabelProps={{shrink: true}}
                                InputProps={{ sx: { borderRadius: '12px' } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Günlük Aktif Bitiş"
                                type="time"
                                value={selectedKampanya.kampanya_aktif_bitis}
                                onChange={(e) => setSelectedKampanya({
                                    ...selectedKampanya,
                                    kampanya_aktif_bitis: e.target.value
                                })}
                                InputLabelProps={{shrink: true}}
                                InputProps={{ sx: { borderRadius: '12px' } }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Autocomplete
                                multiple
                                options={urunler}
                                getOptionLabel={(option) => option.isim}
                                value={selectedKampanya.urunler || []}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={(event, newValue) => {
                                    setSelectedKampanya({...selectedKampanya, urunler: newValue});
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Dahil Olan Ürünler" placeholder="Ürün Seçin" InputProps={{ ...params.InputProps, sx: { borderRadius: '12px' } }}/>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<CloudUploadIcon/>}
                                    sx={{ borderRadius: '12px', height: 48, minWidth: 200 }}
                                >
                                    Görsel Yükle
                                    <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                                </Button>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={selectedKampanya.aktif}
                                            onChange={(e) => setSelectedKampanya({
                                                ...selectedKampanya,
                                                aktif: e.target.checked
                                            })}
                                        />
                                    }
                                    label={<Typography fontWeight={700}>Kampanya Aktif</Typography>}
                                />
                            </Stack>
                            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                {selectedFile ? `Seçilen: ${selectedFile.name}` : 'Mevcut görsel korunacak.'}
                            </Typography>
                        </Grid>

                        {(previewUrl || selectedKampanya.kampanya_gorsel) && (
                            <Grid item xs={12}>
                                <Paper variant="outlined" sx={{ p: 1, borderRadius: '16px', bgcolor: 'grey.50', textAlign: 'center' }}>
                                    <img
                                        src={previewUrl || selectedKampanya.kampanya_gorsel}
                                        alt="Önizleme"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '240px',
                                            borderRadius: '12px',
                                        }}
                                    />
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <Button 
                        variant="outlined" 
                        color="secondary" 
                        onClick={() => setOpenModal(false)}
                    >
                        İptal
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        startIcon={<SaveIcon/>}
                        onClick={handleSave}
                    >
                        Kampanyayı Kaydet
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default KampanyaYonetimi;